import { z } from "zod";
import { getDemoPersonas, resetDemoSession, supabase } from "@/lovable-demo/runtime/client";
import type { AppRole } from "./types";

type UserRoleEntry = { id: string; email: string; name: string; roles: AppRole[] };

async function assertRoleManager() {
  const { data } = await supabase.auth.getUser();
  if (!data.user) throw new Error("Select a synthetic demo persona first.");
  const { data: roles, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", data.user.id)
    .in("role", ["admin", "quality_director"]);
  if (error || !roles?.length)
    throw new Error("Quality Director or administrator demo authority required.");
}

/** Reference personas only. This never lists or changes portal authentication accounts. */
export async function listUsersAndRoles(): Promise<UserRoleEntry[]> {
  await assertRoleManager();
  const { data: roles, error } = await supabase.from("user_roles").select("user_id,role");
  if (error) throw new Error("Synthetic role references could not be loaded.");
  return getDemoPersonas()
    .map((persona) => ({
      id: persona.id,
      email: persona.email,
      name: persona.name,
      roles: (roles ?? [])
        .filter((role) => role.user_id === persona.id)
        .map((role) => role.role as AppRole),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const roleSchema = z.enum([
  "admin",
  "quality_director",
  "quality_engineer",
  "supplier_quality",
  "manufacturing_engineer",
  "viewer",
]);
const changeSchema = z.object({
  userId: z.string().min(1),
  role: roleSchema,
  grant: z.boolean(),
  rationale: z.string().trim().min(10),
});

export async function changeUserRole(input: { data: z.input<typeof changeSchema> }) {
  await assertRoleManager();
  const data = changeSchema.parse(input.data);
  const { error } = await supabase.rpc("manage_user_role", {
    p_user_id: data.userId,
    p_role: data.role,
    p_grant: data.grant,
    p_rationale: data.rationale,
  });
  if (error) throw new Error(error.message);
  return { ok: true as const };
}

export async function resetSampleData(input: { data: { rationale: string } }) {
  await assertRoleManager();
  z.string().trim().min(10).parse(input.data.rationale);
  resetDemoSession();
  return { ok: true as const };
}
